// Precision Storytelling — exercise definitions (frontend content source).
//
// Mirrors copywritingExercises.js exactly: per-module arrays, a combined export,
// getExercisesByModule / getExerciseById helpers, and a MODULES array.
//
// NOTE on the DB split: real rows exist in Supabase `lessons` (ids 4–8) and
// `exercises` (ids 16–20) so `user_progress` FKs resolve and progress persists.
// But `exercises` has no `source_material` column, so the rambling `sourceMaterial`
// prompt text lives ONLY here (same as Copywriting keeps `brief`/`originalCopy`
// frontend-only). The backend receives what it needs at scoring time from the
// hook, not from the DB.
//
// `order` on each exercise === its `exercises.order` within the lesson === the
// `exercise_order` written to `user_progress`. Keep them in sync.

import { STORYTELLING_MODULES } from '../modules.js';

// ── Module 1: The Origin Cut (1 real exercise) ──────────────────────────────
export const ORIGIN_CUT_EXERCISES = [
  {
    id: 'origin-cut-1',
    moduleId: 'origin-cut',
    order: 1,
    title: 'Sourdough to Storefront',
    sourceMaterial:
      "So basically I've always liked baking, like since I was a kid, my grandmother used to bake on Sundays and I'd help her, though I wasn't very good at it honestly, I burned a lot of things. And then I went to college for marketing, which had nothing to do with baking, and I worked in marketing for about six years at two different companies, the second one was actually a decent job, I got promoted twice, but I always felt like something was missing, you know? And then during the pandemic I started baking sourdough like everyone else did, and I posted pictures on Instagram just for fun, and people started asking if they could buy loaves, so I started selling like five loaves a week out of my apartment, which was technically not allowed by my lease but nobody said anything. And then my sister, who's a lawyer, told me I should look into a cottage food license, and I did, and it turned out our state actually makes that pretty easy, and so I got licensed, and I kept doing it on the side for almost two years while still working my marketing job, saving up money, and then last spring my company did layoffs and I was one of the people let go, which was honestly terrifying at the time, I had about four months of savings, but it also kind of forced my hand, because I'd been thinking about doing the bakery full time for a while but was too scared to actually quit, and so I used my severance to rent a small commercial kitchen space, and now I run the bakery full time, we do about 200 loaves a week now, mostly through farmers markets and a few restaurant accounts.",
    instruction:
      'Cut and reshape the story above into under 100 words that lands this exact point: "Sometimes the leap you\'re too scared to take gets taken for you — and that\'s what finally moves you." Cut anything that doesn\'t serve that point, even if it\'s a nice detail.',
    goal: "A good origin story isn't the most detail, it's the least detail that still lands the point.",
    hint: "The actual turning point isn't the grandmother, the marketing job, or the sourdough hobby. It's the layoff.",
    tags: ['Origin Cut', 'Beginner'],
    scoringCriteria: [
      'lands the specific named point about the forced leap',
      'stays at or under 100 words',
      'cuts tangential detail rather than just shortening sentences',
      'keeps enough concrete detail that it still reads as a real story',
    ],
  },
];

// ── Modules 2–5: correctly-shaped placeholders (real content to follow) ──────
const placeholder = (moduleId, label) => [
  {
    id: `${moduleId}-1`,
    moduleId,
    order: 1,
    title: 'Coming soon — exercise to follow',
    sourceMaterial: '',
    instruction: 'Placeholder exercise. Real content for this module is being written — check back soon.',
    goal: 'Placeholder — goal to follow.',
    hint: 'Placeholder — hint to follow.',
    tags: [label, 'Placeholder'],
    scoringCriteria: ['placeholder — scoring criteria to follow'],
    placeholder: true,
  },
];

export const TURNING_POINT_EXERCISES = placeholder('turning-point', 'Turning Point');
export const DATA_STORY_EXERCISES    = placeholder('data-story', 'Data Story');
export const PITCH_ARC_EXERCISES     = placeholder('pitch-arc', 'Pitch Arc');
export const CALLBACK_EXERCISES      = placeholder('callback', 'Callback');

// ── Combined + helpers (same surface as copywritingExercises.js) ────────────
export const ALL_STORYTELLING_EXERCISES = [
  ...ORIGIN_CUT_EXERCISES,
  ...TURNING_POINT_EXERCISES,
  ...DATA_STORY_EXERCISES,
  ...PITCH_ARC_EXERCISES,
  ...CALLBACK_EXERCISES,
];

const BY_MODULE = {
  'origin-cut': ORIGIN_CUT_EXERCISES,
  'turning-point': TURNING_POINT_EXERCISES,
  'data-story': DATA_STORY_EXERCISES,
  'pitch-arc': PITCH_ARC_EXERCISES,
  'callback': CALLBACK_EXERCISES,
};

export const getExercisesByModule = (moduleId) => BY_MODULE[moduleId] ?? [];
export const getExerciseById = (id) => ALL_STORYTELLING_EXERCISES.find((e) => e.id === id);

// Landing-page module cards. Descriptions match the seeded lessons.
const DESCRIPTIONS = {
  'origin-cut': 'Cut a rambling origin story down to the one point it needs to land.',
  'turning-point': 'Isolate the single moment a story turns on.',
  'data-story': 'Wrap data in a narrative of progress.',
  'pitch-arc': 'Structure a pitch from problem to payoff.',
  'callback': 'Close the loop with a callback that recontextualizes the opening.',
};

export const MODULES = STORYTELLING_MODULES.map((m) => ({
  id: m.slug,
  title: m.title,
  description: DESCRIPTIONS[m.slug],
  reps: (BY_MODULE[m.slug] ?? []).length,
  color: m.color,
  order: m.order,
}));
